import type {
  School,
  CreateSchoolInput,
  Course,
  Lesson,
  Batch,
  Channel,
  Thread,
  Message,
  DashboardOverview,
  Certificate,
  AdminUser,
  AdminAnalytics,
  Payment,
  Bookmark,
  Note,
  CalendarEvent,
  Assignment,
} from 'types';

export function mapCreateSchoolInputToDb(input: CreateSchoolInput): Record<string, unknown> {
  return {
    slug: input.slug,
    name: input.name,
    short_name: input.shortName,
    description: input.description,
    icon: input.icon ?? 'book',
    status: 'coming_soon',
    difficulty: input.difficulty,
    estimated_duration: input.estimatedDuration ?? null,
    theme_key: input.themeKey ?? 'default',
    is_premium: input.isPremium ?? false,
    sort_order: input.sortOrder ?? 0,
  };
}

export function mapDbSchool(
  row: Record<string, unknown>,
  studentCount = 0,
  progress?: number
): School {
  return {
    id: row.id as string,
    slug: row.slug as string,
    name: row.name as string,
    shortName: row.short_name as string,
    description: row.description as string,
    icon: row.icon as string,
    status: row.status as School['status'],
    difficulty: row.difficulty as School['difficulty'],
    estimatedDuration: (row.estimated_duration as string) ?? 'Ongoing',
    studentCount,
    progress,
    sortOrder: row.sort_order as number,
    themeKey: row.theme_key as string,
    isPremium: row.is_premium as boolean,
    isActive: row.is_active as boolean | undefined,
    categoryId: row.category_id as string | undefined,
    categoryTitle: row.category_title as string | undefined,
    categoryShortTitle: row.category_short_title as string | undefined,
    categorySort: row.category_sort as number | undefined,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function mapDbCourse(
  row: Record<string, unknown>,
  schoolSlug?: string,
  instructorName?: string
): Course {
  const school = row.core_schools as { slug: string } | null;
  const instructor = row.instructor as { first_name: string; last_name: string } | null;
  return {
    id: row.id as string,
    schoolId: row.school_id as string,
    schoolSlug: schoolSlug ?? school?.slug ?? '',
    title: row.title as string,
    description: (row.description as string) ?? '',
    thumbnailUrl: row.thumbnail_url as string | undefined,
    difficulty: row.difficulty as Course['difficulty'],
    duration: (row.duration as string) ?? '',
    lessonCount: (row.lesson_count as number) ?? 0,
    enrolledCount: (row.enrolled_count as number) ?? 0,
    progress: row.progress as number | undefined,
    instructorName:
      instructorName ??
      (instructor ? `${instructor.first_name} ${instructor.last_name}` : 'Instructor'),
    createdAt: row.created_at as string,
  };
}

export function mapDbLesson(row: Record<string, unknown>): Lesson {
  return {
    id: row.id as string,
    moduleId: row.module_id as string,
    courseId: row.course_id as string,
    title: row.title as string,
    description: (row.description as string) ?? '',
    duration: row.duration_seconds as number,
    videoUrl: row.video_url as string | undefined,
    videoProvider: row.video_provider as Lesson['videoProvider'],
    videoVisibility: row.video_visibility as Lesson['videoVisibility'],
    sortOrder: row.sort_order as number,
    isCompleted: row.is_completed as boolean | undefined,
  };
}

export function mapDbBatch(
  row: Record<string, unknown>,
  schoolSlug?: string,
  teacherName?: string
): Batch {
  const school = row.core_schools as { slug: string } | null;
  const teacher = row.teacher as { first_name: string; last_name: string } | null;
  const topic = row.core_batch_topics as
    | { name_en?: string; is_other?: boolean }
    | null
    | undefined;
  const offeringType = row.offering_type === 'special' ? 'special' : 'regular';
  const topicCustom = typeof row.topic_custom === 'string' ? row.topic_custom.trim() : '';
  let topicLabel: string | null = null;
  if (offeringType === 'special') {
    if (topic?.is_other && topicCustom) topicLabel = topicCustom;
    else if (topicCustom) topicLabel = topicCustom;
    else topicLabel = topic?.name_en ?? null;
  }
  return {
    id: row.id as string,
    schoolId: row.school_id as string,
    schoolSlug: schoolSlug ?? school?.slug ?? '',
    courseId: (row.course_id as string | null) ?? null,
    name: row.name as string,
    description: (row.description as string) ?? '',
    startDate: row.start_date as string,
    endDate: (row.end_date as string) ?? '',
    schedule: (row.schedule as string) ?? '',
    studentCount: (row.student_count as number) ?? 0,
    maxStudents: row.max_students as number,
    teacherName:
      teacherName ?? (teacher ? `${teacher.first_name} ${teacher.last_name}` : 'Teacher'),
    progress: row.progress as number | undefined,
    status: row.status as Batch['status'],
    offeringType,
    topicLabel,
  };
}

export function mapDbChannel(row: Record<string, unknown>): Channel {
  const school = row.core_schools as { slug: string } | null;
  return {
    id: row.id as string,
    name: row.name as string,
    slug: row.slug as string,
    description: (row.description as string) ?? '',
    schoolSlug: school?.slug,
    memberCount: (row.member_count as number) ?? 0,
    isPinned: row.is_pinned as boolean,
  };
}

export function mapDbThread(row: Record<string, unknown>): Thread {
  const author = row.author as { first_name: string; last_name: string } | null;
  return {
    id: row.id as string,
    channelId: row.channel_id as string,
    title: row.title as string,
    authorName: author ? `${author.first_name} ${author.last_name}` : 'Member',
    content: row.content as string,
    replyCount: row.reply_count as number,
    isPinned: row.is_pinned as boolean,
    isAnnouncement: row.is_announcement as boolean,
    createdAt: row.created_at as string,
  };
}

export function mapDbMessage(row: Record<string, unknown>): Message {
  const author = row.author as { first_name: string; last_name: string } | null;
  const mentions = row.stud_message_mentions as
    | { core_users?: { first_name?: string; last_name?: string } | { first_name?: string; last_name?: string }[] }[]
    | undefined;
  const mentionNames =
    mentions?.map((item) => {
      const user = item.core_users;
      const value = Array.isArray(user) ? user[0] : user;
      const full = `${value?.first_name ?? ''} ${value?.last_name ?? ''}`.trim();
      return full || null;
    }).filter((name): name is string => Boolean(name)) ?? [];
  return {
    id: row.id as string,
    threadId: row.thread_id as string,
    authorName: author ? `${author.first_name} ${author.last_name}` : 'Member',
    content: row.content as string,
    mentions: mentionNames,
    createdAt: row.created_at as string,
  };
}

export function mapDbAdminUser(row: Record<string, unknown>, roles: string[]): AdminUser {
  return {
    id: row.id as string,
    email: row.email as string,
    firstName: row.first_name as string,
    lastName: row.last_name as string,
    roles,
    status: (row.is_active === false ? 'inactive' : 'active') as AdminUser['status'],
    lastLoginAt: row.last_login_at as string | undefined,
    createdAt: row.created_at as string,
  };
}

export function mapDbCertificate(row: Record<string, unknown>): Certificate {
  const course = row.inst_courses as { title: string } | null;
  const school = row.core_schools as { name: string } | null;
  const snapshot = (row.brand_snapshot ?? {}) as {
    level?: string | null;
    nameEn?: string | null;
    nameHi?: string | null;
    credentialTitleEn?: string | null;
    credentialTitleHi?: string | null;
    durationMonths?: number | null;
    certificateLanguage?: 'en' | 'hi';
  };
  const lang = (row.certificate_language as 'en' | 'hi' | undefined) ?? snapshot.certificateLanguage ?? 'en';
  const programName = lang === 'hi' ? snapshot.nameHi ?? snapshot.nameEn : snapshot.nameEn ?? snapshot.nameHi;
  const credentialTitle =
    lang === 'hi'
      ? snapshot.credentialTitleHi ?? snapshot.credentialTitleEn
      : snapshot.credentialTitleEn ?? snapshot.credentialTitleHi;

  return {
    id: row.id as string,
    userId: row.user_id as string,
    courseName: course?.title ?? 'Course',
    schoolName: school?.name ?? 'School',
    issuedAt: row.issued_at as string,
    verificationCode: row.verification_code as string,
    pdfUrl: row.pdf_url as string | undefined,
    certificateLanguage: lang,
    programLevel: snapshot.level ?? null,
    programName: programName ?? null,
    credentialTitle: credentialTitle ?? null,
    durationMonths: snapshot.durationMonths ?? null,
  };
}

export function mapDbBookmark(row: Record<string, unknown>): Bookmark {
  const lesson = row.inst_lessons as { title: string } | null;
  const course = row.inst_courses as { title: string } | null;
  return {
    id: row.id as string,
    lessonId: row.lesson_id as string,
    lessonTitle: lesson?.title ?? 'Lesson',
    courseTitle: course?.title ?? 'Course',
    createdAt: row.created_at as string,
  };
}

export function mapDbNote(row: Record<string, unknown>): Note {
  const lesson = row.inst_lessons as { title: string } | null;
  const lessonId = row.lesson_id as string | null | undefined;
  return {
    id: row.id as string,
    lessonId: lessonId ?? undefined,
    lessonTitle: lesson?.title ?? (lessonId ? 'Lesson' : undefined),
    title: (row.title as string | null) ?? undefined,
    content: row.content as string,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function mapDbCalendarEvent(row: Record<string, unknown>): CalendarEvent {
  const batch = row.inst_batches as { name?: string; id?: string } | null;
  const type = row.type as CalendarEvent['type'];
  const batchId = (row.batch_id as string | undefined) ?? batch?.id;
  const filterKind =
    (row.filter_kind as CalendarEvent['filterKind'] | undefined) ??
    (type === 'assignment' || type === 'exam' ? 'due' : 'live');
  const href =
    (row.href as string | undefined) ??
    (batchId ? `/batches/${batchId}` : '/batches');

  return {
    id: row.id as string,
    title: row.title as string,
    type,
    startAt: row.start_at as string,
    endAt: row.end_at as string,
    batchName: batch?.name,
    batchId,
    institutionName: (row.institution_name as string | undefined) ?? undefined,
    filterKind,
    href,
    durationMinutes: (row.duration_minutes as number | undefined) ?? undefined,
  };
}

export function mapDbAssignment(row: Record<string, unknown>): Assignment {
  return {
    id: row.id as string,
    batchId: row.batch_id as string,
    title: row.title as string,
    description: (row.description as string) ?? '',
    dueDate: row.due_date as string,
    maxScore: (row.max_score as number) ?? 100,
    questionCount: (row.question_count as number) ?? 0,
    status: (row.status as Assignment['status']) ?? 'pending',
    score: row.score as number | undefined,
    feedback: (row.feedback as string | null | undefined) ?? undefined,
    submittedAt: (row.submitted_at as string | null | undefined) ?? undefined,
    attemptNumber: (row.attempt_number as number | undefined) ?? undefined,
    answersRevealed: Boolean(row.answers_revealed),
    retakeGranted: Boolean(row.retake_granted),
  };
}

export function mapDbPayment(row: Record<string, unknown>, userName?: string): Payment {
  const user = row.core_users as { first_name: string; last_name: string } | null;
  return {
    id: row.id as string,
    userId: row.user_id as string,
    userName: userName ?? (user ? `${user.first_name} ${user.last_name}` : 'User'),
    amount: Number(row.amount),
    currency: row.currency as string,
    status: row.status as Payment['status'],
    description: (row.description as string) ?? '',
    createdAt: row.created_at as string,
  };
}

// Re-export for dashboard - computed client-side from seed or API
export type { DashboardOverview, AdminAnalytics };
